import { useMemo } from 'react';
import { ActionIcon, Badge, Box, Group, Image, Menu, Paper, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconCheck, IconChefHat, IconCoffee, IconBaguette, IconDotsVertical, IconToolsKitchen2 } from '@tabler/icons-react';
import {
  MealPlan,
  MealPlanAssignment,
  MealType,
  Recipe,
  getAssignmentMealType,
} from '../../types/mealPlan';
import { getReferenceAnchor } from '../../utils/mealPlanHelpers';
import { pencilInImageFor } from '../../utils/pencilInImages';

// The plan regrouped by COOKS instead of calendar slots: each cooking
// session with the meals it feeds. This is the batch cooker's actual
// planning artifact ("Sunday: harissa bake + egg bites batch"), and the
// header ratio is the wizard's cooks-to-meals promise measured against the
// real plan. Laid out on the week's day spine like the other tabs, with
// cook-free days shown as quiet cells: the empty days ARE the payoff.
//
// No per-slot colors: slot badges are neutral gray (the meal-type rainbow
// was purged from the review card and preferences drawer too). The one
// colored badge is "Cooked ahead" in yellow, the app's established
// made-ahead/leftovers language.

const SLOT_META: Record<MealType, { label: string; Icon: typeof IconCoffee }> = {
  breakfast: { label: 'Breakfast', Icon: IconCoffee },
  lunch: { label: 'Lunch', Icon: IconBaguette },
  dinner: { label: 'Dinner', Icon: IconToolsKitchen2 },
};

const SLOT_ORDER: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };

// A cookedOnDate is only honored when it precedes the eating date (drags can
// leave stale values; rendering defensively ignores them)
const cookDateOf = (a: MealPlanAssignment): string =>
  a.cookedOnDate && a.cookedOnDate < a.plannedDate ? a.cookedOnDate : a.plannedDate;

const weekdayNameOf = (dateString: string): string => {
  const [y, m, d] = dateString.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' });
};

const shortDateOf = (dateString: string): string => {
  const [, m, d] = dateString.split('-').map(Number);
  return `${m}/${d}`;
};

const addDays = (dateString: string, delta: number): string => {
  const [y, m, d] = dateString.split('-').map(Number);
  const date = new Date(y, m - 1, d + delta);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${dd}`;
};

type CookScheduleViewProps = {
  mealPlan: MealPlan;
  onOpen: (assignment: MealPlanAssignment, recipe: Recipe) => void;
  // When present, cook rows offer a day picker that moves the cook's labor
  // day (null = cook when first served). Absent = read-only (viewer, past
  // plan).
  onChangeCookDay?: (anchor: MealPlanAssignment, newCookDate: string | null) => void;
};

export const CookScheduleView = ({ mealPlan, onOpen, onChangeCookDay }: CookScheduleViewProps) => {
  const { spineDates, planDates, cooksByDate, pencilsByDate, fedBy, totalMeals, totalCooks, totalPencils, planStartDate } = useMemo(() => {
    const cooks = mealPlan.assignments.filter(
      a => a.source !== 'assignment_reference' && !!a.scaledRecipe
    );
    const references = mealPlan.assignments.filter(a => a.source === 'assignment_reference');

    // Map each reference to the cook it draws from (slot-aware)
    const fed = new Map<string, MealPlanAssignment[]>();
    let resolvedRefs = 0;
    for (const ref of references) {
      const anchor = getReferenceAnchor(ref, mealPlan);
      if (!anchor) continue;
      resolvedRefs++;
      const list = fed.get(anchor.id) ?? [];
      list.push(ref);
      fed.set(anchor.id, list);
    }
    fed.forEach(list => list.sort((x, y) => x.plannedDate.localeCompare(y.plannedDate)));

    // Group cooks by LABOR date (cookedOnDate when cooked ahead), breakfast
    // -> lunch -> dinner within a day
    const byDate = new Map<string, MealPlanAssignment[]>();
    for (const cook of cooks) {
      const key = cookDateOf(cook);
      const list = byDate.get(key) ?? [];
      list.push(cook);
      byDate.set(key, list);
    }
    byDate.forEach(list =>
      list.sort(
        (x, y) => SLOT_ORDER[getAssignmentMealType(x)] - SLOT_ORDER[getAssignmentMealType(y)]
      )
    );

    // The family's own meals (penciled nights, added custom meals). They
    // may or may not involve cooking - "Tacos" does, "Eating out" doesn't -
    // so the schedule names them without claiming either way, rather than
    // letting their day read as "No cooking".
    const pencilsByDate = new Map<string, MealPlanAssignment[]>();
    for (const a of mealPlan.assignments) {
      if (a.source === 'custom_meal' && a.customMeal) {
        const list = pencilsByDate.get(a.plannedDate) ?? [];
        list.push(a);
        pencilsByDate.set(a.plannedDate, list);
      }
    }

    // The week's day spine (every planned date), plus any labor date that
    // falls before it (night-before prep). Cook-free days stay in the spine
    // on purpose: not cooking is the schedule's payoff, not an omission.
    const planDates = Array.from(
      new Set(mealPlan.assignments.map(a => a.plannedDate))
    ).sort();
    const spine = Array.from(
      new Set([...planDates, ...Array.from(byDate.keys())])
    ).sort();

    let pencilCount = 0;
    pencilsByDate.forEach(list => {
      pencilCount += list.length;
    });

    return {
      spineDates: spine,
      planDates,
      cooksByDate: byDate,
      pencilsByDate,
      fedBy: fed,
      totalMeals: cooks.length + resolvedRefs,
      totalCooks: cooks.length,
      totalPencils: pencilCount,
      planStartDate: planDates[0],
    };
  }, [mealPlan]);

  // Which days a cook feeds now lives in the mini week strip below the
  // line, so the copy carries counts and slot mix, not day-name lists
  const coverageLine = (cook: MealPlanAssignment): string => {
    const slot = getAssignmentMealType(cook);
    const feeds = fedBy.get(cook.id) ?? [];
    if (feeds.length === 0) {
      return `${cook.plannedDay}'s ${slot}`;
    }
    if (slot !== 'dinner') {
      const count = feeds.length + 1;
      return `One batch makes ${count} ${slot}${count !== 1 ? 's' : ''}`;
    }
    const dinnerCount = 1 + feeds.filter(f => getAssignmentMealType(f) === 'dinner').length;
    const lunchCount = feeds.filter(f => getAssignmentMealType(f) === 'lunch').length;
    const parts = [
      `${dinnerCount} dinner${dinnerCount !== 1 ? 's' : ''}`,
      lunchCount > 0 ? `${lunchCount} lunch${lunchCount !== 1 ? 'es' : ''}` : null,
    ].filter(Boolean);
    return `Makes ${dinnerCount + lunchCount} meals: ${parts.join(', plus ')}`;
  };

  // Days a cook can move to: from the day before the plan opens (as far
  // back as generation itself places a cook) through the day it's first
  // served. Deliberately not clipped to the freshness window: an explicit
  // move is the user's escape hatch from our placement opinions, same as
  // drag on the meal tabs.
  const cookDayChoices = (cook: MealPlanAssignment): string[] => {
    if (!planStartDate) return [];
    const days: string[] = [];
    for (let d = addDays(planStartDate, -1); d <= cook.plannedDate; d = addDays(d, 1)) {
      days.push(d);
    }
    return days;
  };

  // Tiny coverage strip: one cell per plan day, filled when this row
  // covers that day's meal. One color on purpose: the card's day heading
  // already says when the cooking happens, so the strip answers exactly one
  // question ("which days does it feed?") without arrows across the grid.
  // Every row carries one, so single-day rows read as "just this day" and
  // the batches' multi-fills pop by contrast.
  const weekStrip = (fedDates: Set<string>) => {
    return (
      <Group gap={3} mt={4}>
        {planDates.map(d => {
          const fed = fedDates.has(d);
          return (
            <Box
              key={d}
              title={weekdayNameOf(d)}
              w={18}
              h={18}
              style={{
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 600,
                backgroundColor: fed
                  ? 'var(--mantine-color-orange-light)'
                  : 'transparent',
                color: fed
                  ? 'var(--mantine-color-orange-light-color)'
                  : 'var(--mantine-color-gray-4)',
              }}
            >
              {weekdayNameOf(d)[0]}
            </Box>
          );
        })}
      </Group>
    );
  };

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {spineDates.map(date => {
          const cooks = cooksByDate.get(date) ?? [];
          const pencils = pencilsByDate.get(date) ?? [];
          const beforePlan = planStartDate !== undefined && date < planStartDate;
          const heading = (
            <Group gap={6} align="baseline">
              <Text size="sm" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                {weekdayNameOf(date)}
              </Text>
              <Text size="xs" c="dimmed">
                {shortDateOf(date)}
                {beforePlan && ' · before your week'}
              </Text>
            </Group>
          );

          if (cooks.length === 0 && pencils.length === 0) {
            return (
              <Paper
                key={date}
                radius="lg"
                withBorder
                p="md"
                style={{
                  borderStyle: 'dashed',
                  minHeight: 96,
                }}
              >
                <Stack gap={2}>
                  {heading}
                  <Text size="sm" c="dimmed">
                    No cooking
                  </Text>
                </Stack>
              </Paper>
            );
          }

          return (
            <Paper key={date} radius="lg" p="md" withBorder>
              <Stack gap="sm">
                {heading}
                {cooks.map(cook => {
                  const slot = getAssignmentMealType(cook);
                  const meta = SLOT_META[slot];
                  const recipe = cook.scaledRecipe!;
                  const cookedAhead = cookDateOf(cook) !== cook.plannedDate;
                  const feeds = fedBy.get(cook.id) ?? [];
                  return (
                    <Group
                      key={cook.id}
                      gap="sm"
                      wrap="nowrap"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onOpen(cook, recipe)}
                    >
                      {recipe.imageUrl ? (
                        <Image
                          src={recipe.imageUrl}
                          alt={recipe.name}
                          w={52}
                          h={52}
                          radius="md"
                          fit="cover"
                          style={{ flexShrink: 0 }}
                        />
                      ) : (
                        <Box
                          w={52}
                          h={52}
                          bg="gray.1"
                          style={{
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <meta.Icon size={22} color="var(--mantine-color-gray-6)" />
                        </Box>
                      )}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        {/* Wrapping, not nowrap: on phones the two badges
                            share the width left over by the image and menu,
                            and nowrap ellipsized both. A second line beats
                            "Breakfast · 9 ser..." */}
                        <Group gap={6}>
                          {/* One chip, dot-joined: two side-by-side gray
                              badges blended, and badge-plus-text mixed.
                              No meal count here; the strip below shows it. */}
                          <Badge size="xs" variant="light" color="gray">
                            {meta.label} {'·'} {recipe.servings} servings
                          </Badge>
                          {cookedAhead && (
                            <Badge size="xs" variant="light" color="yellow">
                              Cooked ahead
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" fw={500} truncate>
                          {recipe.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {coverageLine(cook)}
                        </Text>
                        {weekStrip(new Set([cook.plannedDate, ...feeds.map(f => f.plannedDate)]))}
                      </Box>
                      {onChangeCookDay && (
                        <Menu withinPortal position="bottom-end" shadow="md">
                          <Menu.Target>
                            {/* size lg is the touch target (34px); the
                                subtle variant keeps the visual footprint at
                                the glyph, and mr pulls the extra padding
                                back out of the row's right edge */}
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="lg"
                              aria-label="Change cook day"
                              style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                              mr={-8}
                              mt={-6}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconDotsVertical size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          {/* The dropdown portals out of the row's DOM, but
                              React events bubble through the COMPONENT tree,
                              so an item click still reaches the row's
                              open-recipe handler without this */}
                          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                            <Menu.Label>Cook this on</Menu.Label>
                            {cookDayChoices(cook).map(day => {
                              const current = day === cookDateOf(cook);
                              return (
                                <Menu.Item
                                  key={day}
                                  disabled={current}
                                  leftSection={
                                    current ? <IconCheck size={14} /> : <Box w={14} />
                                  }
                                  onClick={() =>
                                    onChangeCookDay(cook, day === cook.plannedDate ? null : day)
                                  }
                                >
                                  {`${weekdayNameOf(day)} ${shortDateOf(day)}${
                                    day === cook.plannedDate ? " · day it's served" : ''
                                  }`}
                                </Menu.Item>
                              );
                            })}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>
                  );
                })}
                {pencils.map(pencil => {
                  const { src, focusY } = pencilInImageFor(pencil.customMeal!.name, getAssignmentMealType(pencil));
                  return (
                    <Group key={pencil.id} gap="sm" wrap="nowrap">
                      <Image
                        src={src}
                        alt={pencil.customMeal!.name}
                        w={52}
                        h={52}
                        radius="md"
                        fit="cover"
                        style={{
                          flexShrink: 0,
                          objectPosition: `center ${focusY}%`,
                          filter: 'saturate(0.7)',
                          opacity: 0.75,
                        }}
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Badge size="xs" variant="light" color="gray">
                          {SLOT_META[getAssignmentMealType(pencil)].label}
                        </Badge>
                        <Text size="sm" fw={500} truncate>
                          {pencil.customMeal!.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Your own meal
                        </Text>
                        {weekStrip(new Set([pencil.plannedDate]))}
                      </Box>
                    </Group>
                  );
                })}
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* The tally closes the view rather than leading it: above the grid it
          pushed the cards down, breaking the top-border alignment the other
          tabs share when switching */}
      <Group gap={6}>
        <IconChefHat size={16} color="var(--mantine-color-dimmed)" />
        <Text size="sm" c="dimmed">
          {totalMeals} meal{totalMeals !== 1 ? 's' : ''} from {totalCooks} cook{totalCooks !== 1 ? 's' : ''}
          {totalPencils > 0 &&
            ` · ${totalPencils} penciled in`}
        </Text>
      </Group>
    </Stack>
  );
};
